import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  CardActions,
  CircularProgress,
  Tooltip,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle,
  Schedule,
  Cancel,
  PlayArrow,
  Timeline as TimelineIcon,
  CalendarMonth as CalendarIcon,
  LocationOn as LocationIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  PlayArrow as DispatchEnabledIcon,
  Pause as DispatchDisabledIcon,
  Security as SafetyIcon,
  Warning as UnsafeIcon,
  HelpOutline as SafetyUnknownIcon,
  CameraAlt as ObservingIcon,
  ToggleOn as AutomationEnabledIcon,
  AccessTime as IdleIcon,
  // Session and operational state icons
  WbSunny as DaytimeIcon,
  WbTwilight as TwilightIcon,
  NightsStay as NightIcon,
  RocketLaunch as StartupIcon,
  AcUnit as CooldownIcon,
  CheckCircleOutline as ReadyIcon,
  Thunderstorm as WeatherUnsafeIcon,
  Build as MaintenanceIcon,
  Visibility as ObservingStateIcon,
} from '@mui/icons-material';
import {
  Announcement as AnnouncementIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import MotdDialog from '../../components/MotdDialog';
import axios from 'axios';

// Helper function for deep equality comparison
const deepEqual = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }
  
  if (typeof a === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key => deepEqual(a[key], b[key]));
  }
  
  return a === b;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    in_progress: 0,
    pending: 0,
    failed: 0,
    aborted: 0,
  });
  const [recentObservations, setRecentObservations] = useState([]);
  const [observatories, setObservatories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [observatoriesLoading, setObservatoriesLoading] = useState(true);
  const [pendingMotds, setPendingMotds] = useState([]);
  const [pendingSystemMotds, setPendingSystemMotds] = useState([]);
  const [selectedMotd, setSelectedMotd] = useState(null);
  const [motdDialogOpen, setMotdDialogOpen] = useState(false);

  // Load pending MOTDs
  useEffect(() => {
    const loadPendingMotds = async () => {
      try {
        const result = await apiService.getPendingMotds();
        if (result.success) {
          setPendingMotds(result.data?.observatories || []);
          setPendingSystemMotds(result.data?.system_motds || []);
        }
      } catch (err) {
        console.error('Failed to load pending MOTDs:', err);
      }
    };
    loadPendingMotds();
  }, []);

  const handleMotdAcknowledged = (motdId) => {
    setPendingMotds(prev => prev.map(obs => ({
      ...obs,
      motds: obs.motds.filter(m => m._id !== motdId)
    })).filter(obs => obs.motds.length > 0));
    setPendingSystemMotds(prev => prev.filter(m => m._id !== motdId));
  };

  const fetchDashboardData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      // Fetch only this user's observations
      const userId = user?._id || user?.id;
      const response = await axios.get('/api/v1/observations', {
        params: { created_by: userId, limit: 100 }
      });
      const data = response.data.data || {};
      const observations = data.observations || data || [];
      
      // Ensure observations is an array
      const observationArray = Array.isArray(observations) ? observations : [];
      
      // Calculate stats
      const stats = {
        total: observationArray.length,
        completed: observationArray.filter(o => o.status === 'completed').length,
        in_progress: observationArray.filter(o => o.status === 'in_progress').length,
        pending: observationArray.filter(o => o.status === 'pending').length,
        failed: observationArray.filter(o => o.status === 'failed').length,
        aborted: observationArray.filter(o => o.status === 'aborted').length,
      };
      
      // Only update state if data has actually changed
      setStats(prevStats => {
        return deepEqual(prevStats, stats) ? prevStats : stats;
      });
      
      const newRecentObs = observationArray.slice(0, 5);
      setRecentObservations(prevObs => {
        return deepEqual(prevObs, newRecentObs) ? prevObs : newRecentObs;
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []);

  const fetchObservatories = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setObservatoriesLoading(true);
      }
      const response = await axios.get('/api/v1/observatories');
      // Only update state if data has actually changed
      const newObservatories = response.data.data?.observatories || response.data.observatories || [];
      setObservatories(prevObs => {
        return deepEqual(prevObs, newObservatories) ? prevObs : newObservatories;
      });
    } catch (err) {
      console.error('Failed to load observatories:', err);
      setObservatories([]);
    } finally {
      if (showLoading) {
        setObservatoriesLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchDashboardData(true);
    fetchObservatories(true);

    // Set up polling - refresh every 15 seconds
    const pollInterval = setInterval(() => {
      fetchDashboardData(false);
      fetchObservatories(false);
    }, 15000); // 15 seconds

    // Cleanup interval on component unmount
    return () => clearInterval(pollInterval);
  }, [fetchDashboardData, fetchObservatories]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'in_progress':
        return <PlayArrow color="info" />;
      case 'pending':
        return <Schedule color="warning" />;
      case 'failed':
        return <Cancel color="error" />;
      default:
        return <Schedule />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'completed_series':
        return 'success';
      case 'partially_completed':
        return 'secondary';
      case 'in_progress':
      case 'acquisition_complete':
        return 'info';
      case 'pending':
      case 'waiting_next':
        return 'warning';
      case 'aborted':
      case 'failed':
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  // Observatory helper functions
  const getObservatoryStatusColor = (status) => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'error';
      default: return 'default';
    }
  };

  const getObservatoryStatusIcon = (status) => {
    return status === 'online' ? <OnlineIcon /> : <OfflineIcon />;
  };

  const getDispatchIcon = (enabled) => {
    return enabled ? <DispatchEnabledIcon /> : <DispatchDisabledIcon />;
  };

  const getDispatchColor = (enabled) => {
    return enabled ? 'success' : 'default';
  };

  const getSafetyIcon = (safetyMonitor) => {
    if (!safetyMonitor?.connected) return <SafetyUnknownIcon />;
    return safetyMonitor.is_safe ? <SafetyIcon /> : <UnsafeIcon />;
  };

  const getSafetyColor = (safetyMonitor) => {
    if (!safetyMonitor?.connected) return 'default';
    return safetyMonitor.is_safe ? 'success' : 'error';
  };

  const getSafetyLabel = (safetyMonitor) => {
    if (!safetyMonitor?.connected) return 'No Safety Monitor';
    return safetyMonitor.is_safe ? 'Safe' : 'Unsafe';
  };

  const getAutomationIcon = (enabled) => {
    return enabled ? <AutomationEnabledIcon /> : <DispatchDisabledIcon />;
  };

  const getAutomationColor = (enabled) => {
    return enabled ? 'success' : 'warning';
  };

  const getAutomationLabel = (enabled) => {
    return enabled ? 'Automation' : 'Manual';
  };

  // Session state helper (calculated from sun position)
  const getSessionStateInfo = (sessionState) => {
    if (!sessionState) return null;
    switch (sessionState) {
      case 'daytime':
        return { icon: DaytimeIcon, label: 'Daytime', color: 'warning' };
      case 'dusk':
        return { icon: TwilightIcon, label: 'Dusk', color: 'info' };
      case 'night':
        return { icon: NightIcon, label: 'Night', color: 'primary' };
      case 'dawn':
        return { icon: TwilightIcon, label: 'Dawn', color: 'info' };
      default:
        return null;
    }
  };

  // Operational state helper (signaled by plugin or derived)
  const getOperationalStateInfo = (opState) => {
    if (!opState) return null;
    switch (opState) {
      case 'unknown':
        return { icon: SafetyUnknownIcon, label: 'Unknown', color: 'default' };
      case 'dusk_startup':
        return { icon: StartupIcon, label: 'Starting Up', color: 'info' };
      case 'dusk_cooldown':
        return { icon: CooldownIcon, label: 'Cooling Down', color: 'info' };
      case 'ready':
        return { icon: ReadyIcon, label: 'Ready', color: 'success' };
      case 'observing':
        return { icon: ObservingStateIcon, label: 'Observing', color: 'primary' };
      case 'weather_hold':
      case 'weather_unsafe':
        return { icon: WeatherUnsafeIcon, label: 'Weather Hold', color: 'warning' };
      case 'shutdown':
        return { icon: NightIcon, label: 'Shutdown', color: 'warning' };
      case 'error':
      case 'equipment_error':
        return { icon: MaintenanceIcon, label: 'Error', color: 'error' };
      case 'maintenance':
        return { icon: MaintenanceIcon, label: 'Maintenance', color: 'warning' };
      // Note: 'online'/'offline' are connection states, not operational states
      // Legacy 'idle' state - don't show chip, it's meaningless
      case 'idle':
      case 'online':
      case 'offline':
        return null;
      default:
        return null;
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {user?.is_first_login ? 'Welcome' : 'Welcome back,'} {user?.name || user?.email || 'Observer'}!
        </Typography>
        {!user?.is_first_login && (
          <Typography variant="body1" color="text.secondary">
            Here's an overview of your observations and recently used observatories
          </Typography>
        )}
      </Box>

      {/* System-wide MOTD Notifications (exclude project/org targeted — those show as popups only) */}
      {(() => {
        const systemWideMotds = pendingSystemMotds.filter(m =>
          !m.target || m.target.target_type === 'all' ||
          (!m.target.project_ids?.length && !m.target.organization_ids?.length)
        );
        return systemWideMotds.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {systemWideMotds.map(motd => (
              <Alert
                key={motd._id}
                severity={motd.severity === 'critical' ? 'error' : motd.severity}
                icon={<AnnouncementIcon />}
                sx={{ mb: 1, cursor: 'pointer' }}
                onClick={() => { setSelectedMotd(motd); setMotdDialogOpen(true); }}
                action={
                  <Button color="inherit" size="small" onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMotd(motd);
                    setMotdDialogOpen(true);
                  }}>
                    View
                  </Button>
                }
              >
                <Typography variant="subtitle2">System: {motd.title}</Typography>
              </Alert>
            ))}
          </Box>
        );
      })()}

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => navigate('/observations/new')}
        >
          Submit New Observation
        </Button>
      </Box>

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Observations
              </Typography>
              <Typography variant="h4">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h4" color="info.main">
                {stats.in_progress}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Failed/Aborted
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.failed + stats.aborted}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Observations */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Recent Observations
          </Typography>
          <Button onClick={() => navigate('/observations')}>
            View All
          </Button>
        </Box>
        
        {recentObservations.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No observations yet
            </Typography>
          </Box>
        ) : (
          <Box>
            {recentObservations.map((obs) => (
              <Box
                key={obs._id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => navigate(`/observations/${obs._id}`, { state: { from: 'dashboard' } })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  {getStatusIcon(obs.status)}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">
                        {obs.targets?.[0]?.name || obs.name || 'Unnamed Target'}
                      </Typography>
                      {(obs.assigned_observatory_name || obs.assigned_to?.name) && (
                        <Typography variant="body2" color="text.secondary">
                          ({obs.assigned_observatory_name || obs.assigned_to?.name})
                        </Typography>
                      )}
                    </Box>
                    {(obs.targets?.[0]?.coordinates || obs.targets?.[0]?.ra != null) && (
                      <Typography variant="body2" color="text.secondary">
                        RA: {(obs.targets?.[0]?.coordinates?.ra ?? obs.targets?.[0]?.ra)?.toFixed(3) || 'N/A'}°,
                        Dec: {(obs.targets?.[0]?.coordinates?.dec ?? obs.targets?.[0]?.dec)?.toFixed(3) || 'N/A'}°
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    label={obs.status}
                    color={getStatusColor(obs.status)}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {new Date(obs.created_at).toLocaleDateString('en-US')}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Observatory Status Section */}
      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Accessible Observatories
            </Typography>
            <Button onClick={() => navigate('/observatories')}>
              View All
            </Button>
          </Box>
          
          {observatoriesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : observatories.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No recently used observatories available
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {observatories.slice(0, 3).map((observatory) => (
                <Grid item xs={12} md={6} lg={4} key={observatory._id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      border: 1,
                      borderColor: 'divider',
                      boxShadow: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 4,
                        borderColor: 'primary.main'
                      }
                    }}
                    onClick={() => navigate(`/observatories/${observatory._id}`, { state: { from: 'dashboard' } })}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Typography variant="h6" component="div">
                          {observatory.name}
                        </Typography>
                        {(() => {
                          const obsMotds = pendingMotds.find(o => o.observatory?._id === observatory._id);
                          if (!obsMotds || obsMotds.motds.length === 0) return null;
                          return (
                            <Tooltip title={`${obsMotds.motds.length} announcement${obsMotds.motds.length > 1 ? 's' : ''}`}>
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedMotd({ ...obsMotds.motds[0], observatory_id: observatory._id });
                                  setMotdDialogOpen(true);
                                }}
                              >
                                <AnnouncementIcon />
                              </IconButton>
                            </Tooltip>
                          );
                        })()}
                      </Box>

                      {observatory.description && (
                        <Typography variant="body2" color="text.secondary" mb={2}>
                          {observatory.description}
                        </Typography>
                      )}

                      {/* Status Badges - unified with ObservatoryDetailsBase */}
                      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                        <Chip
                          icon={getObservatoryStatusIcon(observatory.status)}
                          label={observatory.status || 'unknown'}
                          color={getObservatoryStatusColor(observatory.status)}
                          size="small"
                        />
                        {/* Session State chip (calculated from sun position) - only show when online */}
                        {observatory.status === 'online' && observatory.session_state && (() => {
                          const sessionInfo = getSessionStateInfo(observatory.session_state);
                          if (!sessionInfo) return null;
                          const SessionIcon = sessionInfo.icon;
                          return (
                            <Tooltip title="Session state calculated from sun position">
                              <Chip
                                icon={<SessionIcon />}
                                label={sessionInfo.label}
                                color={sessionInfo.color}
                                size="small"
                              />
                            </Tooltip>
                          );
                        })()}
                        {/* Operational State chip (signaled by plugin) - only show when online */}
                        {observatory.status === 'online' && observatory.operational_state && (() => {
                          const opInfo = getOperationalStateInfo(observatory.operational_state);
                          if (!opInfo) return null;
                          const OpIcon = opInfo.icon;
                          return (
                            <Tooltip title={`Operational State: ${opInfo.label}`}>
                              <Chip
                                icon={<OpIcon />}
                                label={opInfo.label}
                                color={opInfo.color}
                                size="small"
                              />
                            </Tooltip>
                          );
                        })()}
                        {/* Only show operational chips when online */}
                        {observatory.status === 'online' && (
                          <>
                            <Chip
                              icon={getDispatchIcon(observatory.dispatch_enabled !== false)}
                              label={observatory.dispatch_enabled !== false ? 'Dispatching' : 'Paused'}
                              color={getDispatchColor(observatory.dispatch_enabled !== false)}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              icon={getSafetyIcon(observatory.equipment_status?.safetyMonitor)}
                              label={getSafetyLabel(observatory.equipment_status?.safetyMonitor)}
                              color={getSafetyColor(observatory.equipment_status?.safetyMonitor)}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              icon={getAutomationIcon(observatory.system_status?.operations_enabled !== false)}
                              label={getAutomationLabel(observatory.system_status?.operations_enabled !== false)}
                              color={getAutomationColor(observatory.system_status?.operations_enabled !== false)}
                              size="small"
                              variant="outlined"
                            />
                          </>
                        )}
                      </Box>

                      {(observatory.current_observation?.target_name || observatory.current_observation?.name) ? (
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={2}
                          p={1.5}
                          sx={{
                            backgroundColor: 'primary.50',
                            borderLeft: '4px solid',
                            borderColor: 'primary.main',
                            borderRadius: 1
                          }}
                        >
                          <ObservingIcon color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="caption" display="block" color="primary" sx={{ fontWeight: 600 }}>
                              Currently Observing
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {observatory.current_observation.target_name || observatory.current_observation.name}
                            </Typography>
                            {observatory.current_observation.completion_percentage > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {observatory.current_observation.completion_percentage}% complete
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={2}
                          p={1.5}
                          sx={{
                            backgroundColor: 'action.hover',
                            borderLeft: '4px solid',
                            borderColor: 'action.disabled',
                            borderRadius: 1
                          }}
                        >
                          <IdleIcon color="action" fontSize="small" />
                          <Box>
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Status
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Idle
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {observatory.location?.latitude?.toFixed(2)}°, {observatory.location?.longitude?.toFixed(2)}°
                        </Typography>
                      </Box>

                      {observatory.location?.timezone && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Timezone:</strong> {observatory.location.timezone}
                        </Typography>
                      )}

                      {observatory.configuration?.telescope_config?.focal_length_mm && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          <strong>Telescope:</strong>{' '}
                          {observatory.configuration.telescope_config.aperture_mm}mm f/
                          {(observatory.configuration.telescope_config.focal_length_mm /
                            observatory.configuration.telescope_config.aperture_mm).toFixed(1)}
                          {' '}({observatory.configuration.telescope_config.focal_length_mm}mm)
                        </Typography>
                      )}

                      {observatory.capabilities?.available_filters && (
                        <Box mt={2}>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Available Filters:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {observatory.capabilities.available_filters.map((filter) => (
                              <Chip key={filter} label={filter} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {observatory.capabilities?.available_instruments && observatory.capabilities.available_instruments.length > 0 && (
                        <Box mt={1}>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Instruments:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {observatory.capabilities.available_instruments.map((instrument) => (
                              <Chip key={instrument} label={instrument} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        size="small"
                        startIcon={<TimelineIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/timeline/${observatory._id}`);
                        }}
                      >
                        Tonight
                      </Button>
                      <Button
                        size="small"
                        startIcon={<CalendarIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/multi-day-timeline/${observatory._id}`);
                        }}
                      >
                        Multi-Day
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          
          {observatories.length > 3 && (
            <Box sx={{ textAlign: 'center', pt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/observatories')}
              >
                View {observatories.length - 3} More Observatories
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      <MotdDialog
        open={motdDialogOpen}
        onClose={() => { setMotdDialogOpen(false); setSelectedMotd(null); }}
        motd={selectedMotd}
        onAcknowledged={handleMotdAcknowledged}
      />
    </Container>
  );
};

export default Dashboard;
